import { memo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Chip } from "@remo-co/ui-core/src/components/Chip";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Close as CloseIcon } from "@remo-co/ui-core/src/icons/Close";
import RaiseHandIcon from "modules/eventButtons/assets/RaiseHand.png";
import { selectIsEventManager } from "modules/event/selectors";
import { useStyles } from "modules/broadcast/BroadcastTotalRequests.styles";
import { selectRaisedHandCount } from "modules/broadcastRaiseHand/redux/selectors";
import { usePrevious } from "../../helpers/reactHooksUtils";

interface IBroadcastTotalRequestsProps {
  onView: () => void;
}

const BroadcastTotalRequests = ({ onView }: IBroadcastTotalRequestsProps) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(true);
  const hasEventManagerAccess = useSelector(selectIsEventManager);
  const { t } = useI18n();
  const raisedHandUsersCount = useSelector(selectRaisedHandCount);
  const prevRaisedHandUsersCount = usePrevious(raisedHandUsersCount) ?? 0;

  useEffect(() => {
    if (raisedHandUsersCount > prevRaisedHandUsersCount) {
      setIsOpen(true);
    }
  }, [prevRaisedHandUsersCount, raisedHandUsersCount, setIsOpen]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOnView = useCallback(() => {
    setIsOpen(false);
    onView();
  }, [onView, setIsOpen]);

  if (raisedHandUsersCount === 0 || !hasEventManagerAccess || !isOpen)
    return null;

  const chipLabel = (
    <>
      {`${t("requests")} (${raisedHandUsersCount})`}
      <Button
        onClick={handleOnView}
        className={styles.viewButton}
        variant="tertiary"
        size="sm"
      >
        {t("view")}
      </Button>
    </>
  );

  return (
    <Chip
      classes={styles}
      avatar={
        <IconButton
          size="small"
          onClick={handleOnView}
          aria-label={t("view.raised.hands")}
        >
          <img className={styles.avatar} src={RaiseHandIcon} alt="" />
        </IconButton>
      }
      label={chipLabel}
      clickable={false}
      onDelete={onClose}
      deleteIcon={
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t("close.raised.hands.popup")}
        >
          <CloseIcon className={styles.delete} data-testid="close-icon" />
        </IconButton>
      }
    />
  );
};

export default memo(BroadcastTotalRequests);
